import React, { useState, useEffect, useMemo } from "react";
import { utils } from "react-modern-calendar-datepicker";
import "react-modern-calendar-datepicker/lib/DatePicker.css";
import Table from "../../components/Table/table";
import Loading from "../../components/Loading/loading";
import { Link } from "react-router-dom";
import "./TehranInventory.css";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import "react-multi-date-picker/styles/layouts/mobile.css";

const INVENTORY_API_URL ="/api/gettehraninventorydata";
const CURRENCY_API_URL ="/api/getcurrencypricesdata";


function toFarsiDigits(num) {
  if (num === null || num === undefined) return "";
  const farsiDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  return num.toString().replace(/[0-9]/g, (x) => farsiDigits[x]);
}

const TehranInventory = () => {
  const [data, setData] = useState([]);
  const [lastCurrency, setLastCurrency] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Tracks which SIZES (with priced items) are showing unpriced items
  const [sizesShowingUnpriced, setSizesShowingUnpriced] = useState([]); 
  const [selectedDay, setSelectedDay] = useState(utils("fa").getToday());

  // --- 1. Grouping and sorting data ---
  const { pricedProductsBySize, unpricedProductsBySize, allSortedSizes } =
    useMemo(() => {
      if (!data || data.length === 0)
        return {
          pricedProductsBySize: {},
          unpricedProductsBySize: {},
          allSortedSizes: [],
        };

      const sortByTitle = (a, b) => (a.title || "").localeCompare(b.title || "");
      
      const pricedProducts = data
        .filter((p) => p.price && Number(p.price) !== 0)
        .sort(sortByTitle);
        
      const unpricedProducts = data
        .filter((p) => !p.price || Number(p.price) === 0)
        .sort(sortByTitle);

      const groupBySize = (products) =>
        products.reduce((acc, product) => {
          const size = product.size || "Unsized";
          if (!acc[size]) acc[size] = [];
          acc[size].push(product);
          return acc;
        }, {});

      const pricedGrouped = groupBySize(pricedProducts);
      const unpricedGrouped = groupBySize(unpricedProducts);
      
      const allSizes = new Set([
        ...Object.keys(pricedGrouped),
        ...Object.keys(unpricedGrouped),
      ]);
      
      const sortedSizes = Array.from(allSizes).sort((a, b) => {
        if (a === "Unsized") return 1;
        if (b === "Unsized") return -1;
        return Number(a) - Number(b);
      });

      return {
        pricedProductsBySize: pricedGrouped,
        unpricedProductsBySize: unpricedGrouped,
        allSortedSizes: sortedSizes,
      };
    }, [data]);

  // --- 2. Logic for displayed products (Priced vs Unpriced Toggle) ---
  const { productsBySize, sortedSizes, sizeClickability } = useMemo(() => {
    const combinedProductsBySize = {};
    const effectiveSizes = new Set();
    const clickability = {}; 
    
    const isSizeUnpricedVisible = (size) => sizesShowingUnpriced.includes(size);
    
    allSortedSizes.forEach((size) => {
      const priced = pricedProductsBySize[size] || [];
      const unpriced = unpricedProductsBySize[size] || [];
      const hasPriced = priced.length > 0;
      const hasUnpriced = unpriced.length > 0;

      if (hasPriced) {
        // Case 1: Has price. Clickable. Toggles unpriced visibility.
        clickability[size] = true; 
        if (isSizeUnpricedVisible(size)) {
          combinedProductsBySize[size] = [...priced, ...unpriced];
        } else {
          combinedProductsBySize[size] = priced;
        }
      } else if (hasUnpriced) {
        // Case 2: Only unpriced. Not clickable. Always show.
        clickability[size] = false; 
        combinedProductsBySize[size] = unpriced;
      } else {
        clickability[size] = false;
        combinedProductsBySize[size] = [];
      }
      
      if (combinedProductsBySize[size].length > 0) {
        effectiveSizes.add(size);
      }
    });

    const sortedEffectiveSizes = allSortedSizes.filter(size => effectiveSizes.has(size));
    
    return {
      productsBySize: combinedProductsBySize,
      sortedSizes: sortedEffectiveSizes,
      sizeClickability: clickability,
    };
  }, [sizesShowingUnpriced, pricedProductsBySize, unpricedProductsBySize, allSortedSizes]);

  // --- 3. Click handler for Table title ---
  const handleTitleClick = (size) => {
      const hasPriced = (pricedProductsBySize[size] || []).length > 0;
      if (!hasPriced) return; 

      setSizesShowingUnpriced(prevSizes => {
          if (prevSizes.includes(size)) {
              return prevSizes.filter(s => s !== size);
          } else {
              return [...prevSizes, size];
          }
      });
  }

  // --- 4. Helper: Format Date for API ---
  const formatToAPIDate = (jalaliDate) =>
    `${jalaliDate.year}-${String(jalaliDate.month).padStart(2, "0")}-${String(
      jalaliDate.day
    ).padStart(2, "0")}`;

  // --- 5. Effect: Fetch Currency (Every 60 Seconds) ---
  useEffect(() => {
    const fetchCurrencyData = () => {
      fetch(CURRENCY_API_URL)
        .then((res) => res.json())
        .then((currencyData) => {
          if (currencyData && currencyData.length > 0)
            setLastCurrency(currencyData[currencyData.length - 1]);
        })
        .catch(console.error);
    };

    fetchCurrencyData(); // Initial load
    const interval = setInterval(fetchCurrencyData, 60000); // Repeat every 60s
    return () => clearInterval(interval); // Cleanup
  }, []);

  // --- 6. Effect: Fetch Inventory (On Date Change OR Every 60 Seconds) ---
  useEffect(() => {
    if (!selectedDay) return;

    // isBackgroundRefresh = true means "don't show the loading spinner"
    const fetchInventoryData = async (isBackgroundRefresh = false) => {
      // Show loading spinner ONLY if this is a manual date change (not a background refresh)
      if (!isBackgroundRefresh) setIsLoading(true);
      
      setError(null);
      
      const formattedDate = formatToAPIDate(selectedDay);
      const urlWithDate = `${INVENTORY_API_URL}?date=${formattedDate}`;
      
      try {
        const response = await fetch(urlWithDate);
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const jsonData = await response.json();
        setData(jsonData);
      } catch (e) {
        console.error("Failed to fetch data:", e);
        // Only show the error message on screen if this was a manual user action
        if (!isBackgroundRefresh) {
            setError("Failed to load inventory data for the selected date.");
            setData([]); 
        }
      } finally {
        if (!isBackgroundRefresh) setIsLoading(false);
      }
    };

    // A. Run immediately when selectedDay changes (show spinner)
    fetchInventoryData(false);

    // B. Run every 60 seconds (hide spinner)
    const interval = setInterval(() => fetchInventoryData(true), 60000);

    // C. Cleanup interval when component unmounts or date changes
    return () => clearInterval(interval);
  }, [selectedDay]);

  // --- 7. Handle Date Picker Change ---
  const handleDateChange = (dateObject) => {
    if (!dateObject) return;
    const jalaliDate = {
      year: Number(dateObject.year),
      month: Number(dateObject.month.number || dateObject.month),
      day: Number(dateObject.day),
    };
    setSelectedDay(jalaliDate);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Link to="/home" className="header-link">خانه</Link>
        <p className="page-title">موجودی انبار تهران</p>
        <div className="header-right-group">
          <div className="date-picker-wrapper">
            <DatePicker
              value={`${selectedDay.year}/${selectedDay.month}/${selectedDay.day}`}
              onChange={handleDateChange}
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-right" 
              inputClass="custom-date-input" 
            />
          </div>
        </div>
      </div>

      <section>
        <h2 className="section-title">میلگرد</h2>
        {isLoading ? (
          <Loading />
        ) : error ? (
          <p className="dashboard-message error">{error}</p>
        ) : sortedSizes.length === 0 ? (
          <p className="dashboard-message">
            هیچ کالایی برای تاریخ انتخاب شده یافت نشد.
          </p>
        ) : (
          <div className="grid-container">
            {sortedSizes.map((size) => (
              <Table
                key={size}
                size={size}
                title={"سایز: " + toFarsiDigits(size)}
                products={productsBySize[size]}
                onTitleClick={
                  sizeClickability[size] ? () => handleTitleClick(size) : undefined
                } 
                isUnpricedShown={sizesShowingUnpriced.includes(size)} 
              />
            ))}
          </div>
        )}
      </section>

      <div className="floating-footer">
        <p className="pill">
          سکه:{" "}
          {lastCurrency
            ? toFarsiDigits((lastCurrency.gold_coin_price / 10).toLocaleString())
            : "..."}
        </p>
        <p className="pill">
          دلار:{" "}
          {lastCurrency
            ? toFarsiDigits((lastCurrency.dollar_price / 10).toLocaleString())
            : "..."}
        </p>
        <p className="pill">
          طلا:{" "}
          {lastCurrency
            ? toFarsiDigits((lastCurrency.gold_gram_price / 10).toLocaleString())
            : "..."}
        </p>
        <p className="pill">
          بورس:{" "}
          {lastCurrency
            ? toFarsiDigits(lastCurrency.stock_index.toLocaleString())
            : "..."}
        </p>
        <p className="pill pill-ons">
          انس:{" "}
          {lastCurrency && lastCurrency.ounce_price
            ? toFarsiDigits(lastCurrency.ounce_price.toLocaleString())
            : "..."}
        </p>
      </div>
    </div>
  );
};

export default TehranInventory;